/**
 * Selection event types
 */
export type SelectionEventType = 'selectionChange' | 'selectionComplete' | 'selectionClear' | 'rightClick';

/**
 * Listener for selection events
 */
export type SelectionListener = (eventType: SelectionEventType, data: any) => void;

/**
 * Data structure for selected elements
 */
export interface ElementSelectionData {
  /** Unique ID of the element */
  id: string;
  
  /** ID of the parent segment */
  segmentId: string;
  
  /** ID of the parent paragraph */
  paragraphId: string;
  
  /** Type of element (text, punct, etc.) */
  type: string;
  
  /** Text content of the element */
  value: string;
  
  /** The actual DOM element (optional) */
  element?: HTMLElement;
}

/**
 * Manages selection of transcript elements in the DOM
 * 
 * This manager handles mouse interactions with transcript elements and maintains
 * the visual selection state. It provides an event system to notify listeners
 * about selection changes.
 */
export class TranscriptSelectionManager {
  /** Container element that holds all transcript elements */
  private container: HTMLElement;
  
  /** Optional reference to the selection service for direct updates */
  private selectionService?: any;

  /** The first element clicked in a selection (anchor point) */
  private anchorElement: HTMLElement | null = null;
  
  /** Index of the anchor element in the elements array */
  private anchorIndex = -1;

  /** Flag indicating if a selection is in progress */
  private isSelecting = false;
  
  /** Currently selected elements */
  private selectedElements: HTMLElement[] = [];
  
  /** Map of event listeners by event type */
  private listeners: Map<SelectionEventType, SelectionListener[]> = new Map();

  /** Bound event handlers for proper event listener removal */
  private readonly handleMouseDownBound: (e: MouseEvent) => void;
  private readonly handleMouseMoveBound: (e: MouseEvent) => void;
  private readonly handleMouseUpBound: (e: MouseEvent) => void;
  private readonly handleContextMenuBound: (e: MouseEvent) => void;

  /** Flag to prevent notification recursion */
  private isNotifying = false;
  
  /** Flag to prevent recursion during clearing */
  private isClearing = false;
  
  /** Flag to prevent recursion during select-by-id */
  private isSelectingById = false;

  /**
   * Create a new TranscriptSelectionManager
   * 
   * @param container The DOM element containing transcript elements
   * @param selectionService Optional reference to the selection service for direct updates
   */
  constructor(container: HTMLElement, selectionService?: any) {
    this.container = container;
    this.selectionService = selectionService;

    // Bind once so we can properly remove later
    this.handleMouseDownBound = this.handleMouseDown.bind(this);
    this.handleMouseMoveBound = this.handleMouseMove.bind(this);
    this.handleMouseUpBound = this.handleMouseUp.bind(this);
    this.handleContextMenuBound = this.handleContextMenu.bind(this);

    // Initialize listener collections
    this.listeners.set('selectionChange', []);
    this.listeners.set('selectionComplete', []);
    this.listeners.set('selectionClear', []);
    this.listeners.set('rightClick', []);

    // Listen for mouse interaction *inside* the container
    this.container.addEventListener("mousedown", this.handleMouseDownBound);
    document.addEventListener("mousemove", this.handleMouseMoveBound);
    document.addEventListener("mouseup", this.handleMouseUpBound);
    this.container.addEventListener("contextmenu", this.handleContextMenuBound);
  }

  /**
   * Set the selection service instance
   * 
   * @param selectionService The selection service to be used
   */
  setSelectionService(selectionService: any) {
    this.selectionService = selectionService;
  }

  /**
   * Register an event listener
   * 
   * @param eventType The type of event to listen for
   * @param listener The callback function
   */
  addEventListener(eventType: SelectionEventType, listener: SelectionListener) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType)?.push(listener);
  }

  /**
   * Remove an event listener
   * 
   * @param eventType The type of event to stop listening for
   * @param listener The callback function to remove
   */
  removeEventListener(eventType: SelectionEventType, listener: SelectionListener) {
    if (!this.listeners.has(eventType)) return;
    
    const listeners = this.listeners.get(eventType);
    if (!listeners) return;
    
    const index = listeners.indexOf(listener);
    if (index !== -1) {
      listeners.splice(index, 1);
    }
  }

  /**
   * Notify all listeners of an event
   * 
   * @param eventType The type of event that occurred
   * @param data Data associated with the event
   */
  private notifyListeners(eventType: SelectionEventType, data: any) {
    // Prevent recursive notifications
    if (this.isNotifying) return;
    
    this.isNotifying = true;
    
    try {
      const listeners = this.listeners.get(eventType);
      if (!listeners) return;
      
      for (const listener of listeners) {
        try {
          listener(eventType, data);
        } catch (error) {
          console.error(`Error in selection listener for ${eventType}:`, error);
        }
      }
      
      // If we have a direct reference to selectionService, update it
      if (this.selectionService && eventType === 'selectionComplete') {
        this.updateSelectionService();
      }
    } finally {
      this.isNotifying = false;
    }
  }

  /**
   * Update the selection service with current selection data
   * This provides direct integration with the app state
   */
  private updateSelectionService() {
    if (!this.selectionService) return;
    
    const selectionData = this.getSelectionData();
    if (!selectionData || selectionData.length === 0) {
      // Clear selection in the service
      if (this.selectionService.clearAllSelections) {
        this.selectionService.clearAllSelections();
      }
      return;
    }

    try {
      // First clear any existing selection
      if (this.selectionService.clearAllSelections) {
        this.selectionService.clearAllSelections();
      }

      // Get transcript model references for each selected element
      const elements = selectionData.map(selData => {
        try {
          // Find the corresponding element in the data model
          // This assumes the selection service has access to the container
          const segment = this.selectionService.getSegment?.(selData.segmentId);
          if (!segment) return null;
          
          const paragraph = segment.getParagraph?.(selData.paragraphId);
          if (!paragraph) return null;
          
          const element = paragraph.getElement?.(selData.id);
          if (!element) return null;
          
          return {
            ...element,
            segmentId: selData.segmentId,
            paragraphId: selData.paragraphId
          };
        } catch (e) {
          console.error('Error finding element in selection service:', e);
          return null;
        }
      }).filter(el => el !== null);

      // Update selection in the service
      if (elements.length > 0 && this.selectionService.selectElements) {
        this.selectionService.selectElements(elements, true);
      }
    } catch (error) {
      console.error('Error updating selection service:', error);
    }
  }

  /**
   * Clean up all event listeners - call when component unmounts
   */
  cleanup() {
    this.container.removeEventListener("mousedown", this.handleMouseDownBound);
    document.removeEventListener("mousemove", this.handleMouseMoveBound);
    document.removeEventListener("mouseup", this.handleMouseUpBound);
    this.container.removeEventListener("contextmenu", this.handleContextMenuBound);
    
    // Clear all listeners
    this.listeners.clear();
  }

  /**
   * Get all transcript elements in the container
   * 
   * @returns Array of transcript element DOM nodes
   */
  private getTranscriptElements(): HTMLElement[] {
    return Array.from(
      this.container.querySelectorAll<HTMLElement>(".transcript-element")
    );
  }
  
  /**
   * Find the nearest transcript element to a given point
   * 
   * @param x Client X coordinate
   * @param y Client Y coordinate
   * @returns The nearest transcript element or null if none found
   */
  findNearestElement(x: number, y: number): HTMLElement | null {
    // Get all transcript elements
    const elements = this.getTranscriptElements();
    if (elements.length === 0) return null;
    
    // Find closest element based on distance calculation
    let closestElement: HTMLElement | null = null;
    let minDistance = Infinity;
    
    elements.forEach(element => {
      const rect = element.getBoundingClientRect();
      
      // Calculate center point of element
      const elementX = rect.left + (rect.width / 2);
      const elementY = rect.top + (rect.height / 2);
      
      // Calculate Euclidean distance
      const distance = Math.sqrt(
        Math.pow(x - elementX, 2) + 
        Math.pow(y - elementY, 2)
      );
      
      // Update closest if this is closer
      if (distance < minDistance) {
        minDistance = distance;
        closestElement = element;
      }
    });
    
    return closestElement;
  }

  /**
   * Find the closest transcript element parent of a given element
   * 
   * @param el The starting element to search from
   * @returns The closest transcript element or null if not found
   */
  private findClosestTranscriptElement(el: HTMLElement | null): HTMLElement | null {
    if (!el) return null;
    let node: HTMLElement | null = el;
    while (node && node !== this.container) {
      if (node.classList.contains("transcript-element")) return node;
      node = node.parentElement;
    }
    return null;
  }

  /**
   * Clear all selection highlights
   */
  private clearSelectionHighlights() {
    if (this.isClearing || this.selectedElements.length === 0) return;
    
    this.isClearing = true;
    
    try {
      this.selectedElements.forEach((el) => el.classList.remove("selected"));
      this.selectedElements = [];
      
      // Only notify if we're not already in a notification cycle
      if (!this.isNotifying && !this.isSelectingById) {
        this.notifyListeners('selectionClear', null);
      }
    } finally {
      this.isClearing = false;
    }
  }

  /**
   * Update the selection based on the current and anchor indices
   * 
   * @param currentIndex Index of the current element in the selection
   */
  private updateSelection(currentIndex: number) {
    const elements = this.getTranscriptElements();
    if (this.anchorIndex === -1 || currentIndex === -1) return;

    // Ensure [start, end] inclusive range regardless of drag direction
    const start = Math.min(this.anchorIndex, currentIndex);
    const end = Math.max(this.anchorIndex, currentIndex);
    
    // First check if selection range hasn't changed
    const oldSelectionLength = this.selectedElements.length;
    const newSelectionLength = end - start + 1;
    
    // Only proceed with update if selection has changed
    if (oldSelectionLength !== newSelectionLength || 
        (this.selectedElements.length > 0 && 
         elements.indexOf(this.selectedElements[0]) !== start)) {
      
      // Instead of clearing and then re-adding, we'll implement an efficient
      // update that only modifies what has changed
      
      // First, remove the 'selected' class from any currently selected elements
      // that won't be in the new selection
      this.selectedElements.forEach(el => {
        const index = elements.indexOf(el);
        if (index < start || index > end) {
          el.classList.remove('selected');
        }
      });
      
      // Create new array for selected elements
      const newSelectedElements = [];
      
      // Add elements from start to end, adding the 'selected' class if needed
      for (let i = start; i <= end; i++) {
        const el = elements[i];
        if (!el.classList.contains('selected')) {
          el.classList.add('selected');
        }
        newSelectedElements.push(el);
      }
      
      // Update the selected elements array
      this.selectedElements = newSelectedElements;
      
      // Notify listeners that selection has changed
      const selectionData = this.getSelectionData();
      this.notifyListeners('selectionChange', { 
        selectedElements: selectionData,
        isComplete: false 
      });
    }
  }

  /**
   * Handle mousedown event to start selection
   * 
   * @param e MouseEvent
   */
  private handleMouseDown(e: MouseEvent) {
    // Only handle left click and inside container
    if (e.button !== 0) return;

    const target = e.target as HTMLElement;
    const element = this.findClosestTranscriptElement(target);
    
    // If clicking directly on an element
    if (element) {
      const elements = this.getTranscriptElements();
      const index = elements.indexOf(element);
      if (index === -1) return;

      // Start new selection
      this.isSelecting = true;
      this.anchorElement = element;
      this.anchorIndex = index;

      // Prevent text caret placement that fights with our custom highlight
      e.preventDefault();

      // Highlight just the anchor for now
      this.updateSelection(index);
    } else {
      // Clicking on empty space - find nearest element
      const nearestElement = this.findNearestElement(e.clientX, e.clientY);
      if (!nearestElement) return;
      
      const elements = this.getTranscriptElements();
      const index = elements.indexOf(nearestElement);
      if (index === -1) return;
      
      // Start selection with nearest element
      this.isSelecting = true;
      this.anchorElement = nearestElement;
      this.anchorIndex = index;
      
      e.preventDefault();
      
      // Highlight the anchor
      this.updateSelection(index);
    }
  }

  /**
   * Handle mousemove event to update selection
   * 
   * @param e MouseEvent
   */
  private handleMouseMove(e: MouseEvent) {
    if (!this.isSelecting) return;

    const target = e.target as HTMLElement;
    const element = this.findClosestTranscriptElement(target);
    const elements = this.getTranscriptElements();

    // If we're directly over an element, use it
    if (element) {
      const currentIndex = elements.indexOf(element);
      if (currentIndex !== -1) {
        this.updateSelection(currentIndex);
      }
    } else {
      // If we're over empty space, find nearest element
      const nearestElement = this.findNearestElement(e.clientX, e.clientY);
      if (nearestElement) {
        const currentIndex = elements.indexOf(nearestElement);
        if (currentIndex !== -1) {
          this.updateSelection(currentIndex);
        }
      }
    }
  }

  /**
   * Handle mouseup event to complete selection
   * 
   * @param e MouseEvent
   */
  private handleMouseUp(e: MouseEvent) {
    if (e.button !== 0 || !this.isSelecting) return;
    
    // Store current isSelecting state before changing it
    const wasSelecting = this.isSelecting;
    this.isSelecting = false;

    // Only update the selection if we were previously in selection mode
    if (wasSelecting) {
      // Get the current element under the cursor
      const target = e.target as HTMLElement;
      const element = this.findClosestTranscriptElement(target);
      const elements = this.getTranscriptElements();
      
      if (element) {
        // Directly over an element
        const currentIndex = elements.indexOf(element);
        
        // If we found a valid current index, update the selection one last time
        if (currentIndex !== -1) {
          this.updateSelection(currentIndex);
        }
      } else {
        // Over empty space - find nearest element
        const nearestElement = this.findNearestElement(e.clientX, e.clientY);
        if (nearestElement) {
          const currentIndex = elements.indexOf(nearestElement);
          if (currentIndex !== -1) {
            this.updateSelection(currentIndex);
          }
        }
      }
      
      // Notify listeners that selection is complete, but only if we have a selection
      if (this.selectedElements.length > 0) {
        const selectionData = this.getSelectionData();
        this.notifyListeners('selectionComplete', { 
          selectedElements: selectionData,
          position: { x: e.clientX, y: e.clientY }
        });
      }
    }
  }

  /**
   * Handle right-click (context menu) events
   * 
   * @param e MouseEvent
   */
  private handleContextMenu(e: MouseEvent) {
    if (e.button !== 2) return;
    
    // Prevent default browser context menu
    e.preventDefault();
    
    // Get the clicked element
    const target = e.target as HTMLElement;
    const element = this.findClosestTranscriptElement(target);
    
    // If directly over an element
    if (element) {
      // If we don't have a selection yet, select this element
      if (!this.hasSelection()) {
        // Create a fresh selection with just this element
        this.selectedElements = [];
        element.classList.add("selected");
        this.selectedElements.push(element);
      }
    } else {
      // If over empty space, find nearest element
      const nearestElement = this.findNearestElement(e.clientX, e.clientY);
      
      if (nearestElement && !this.hasSelection()) {
        // Create a fresh selection with the nearest element
        this.selectedElements = [];
        nearestElement.classList.add("selected");
        this.selectedElements.push(nearestElement);
      }
    }
    
    // Get selection data and notify listeners
    const selectionData = this.getSelectionData();
    this.notifyListeners('rightClick', {
      selectedElements: selectionData,
      position: { x: e.clientX, y: e.clientY },
      originalEvent: e
    });
  }

  /**
   * Returns data about each selected element
   * 
   * @returns Array of selected element data or empty array if no selection
   */
  getSelectionData(): ElementSelectionData[] {
    if (this.selectedElements.length === 0) return [];

    return this.selectedElements.map((el) => ({
      id: el.getAttribute("data-element-id") || "",
      segmentId: el.getAttribute("data-segment-id") || "",
      paragraphId: el.getAttribute("data-paragraph-id") || "",
      type: el.getAttribute("data-element-type") || "",
      value: el.textContent || "",
      element: el // Include the DOM element for convenience
    }));
  }

  /**
   * Clear selection visually & internally
   */
  clearSelection() {
    this.clearSelectionHighlights();
    this.anchorElement = null;
    this.anchorIndex = -1;
    this.isSelecting = false;
  }

  /**
   * Check if we currently have any selected elements
   * 
   * @returns True if there are selected elements, false otherwise
   */
  hasSelection(): boolean {
    return this.selectedElements.length > 0;
  }

  /**
   * Select elements by ID
   * 
   * @param elementIds Array of element IDs to select
   */
  selectElementsByIds(elementIds: string[]) {
    if (!elementIds.length || this.isSelectingById) return;
    
    // Check if selection is already exactly the same
    if (this.selectedElements.length === elementIds.length) {
      const currentIds = this.selectedElements.map(el => 
        el.getAttribute("data-element-id")).filter(Boolean);
      
      // If all current IDs are in the requested selection and vice versa, do nothing
      const allCurrentInRequested = currentIds.every(id => elementIds.includes(id || ""));
      const allRequestedInCurrent = elementIds.every(id => 
        currentIds.includes(id));
        
      if (allCurrentInRequested && allRequestedInCurrent) {
        return; // Already have exactly this selection
      }
    }
    
    this.isSelectingById = true;
    
    try {
      // Create a Set for faster lookups
      const idSet = new Set(elementIds);
      
      // Get all transcript elements
      const elements = this.getTranscriptElements();
      
      // First, identify elements to keep and those to remove selection from
      const elementsToSelect = [];
      const currentSelectedIds = new Set(
        this.selectedElements.map(el => el.getAttribute("data-element-id")).filter(Boolean)
      );
      
      // Remove 'selected' class from elements that shouldn't be selected anymore
      this.selectedElements.forEach(el => {
        const id = el.getAttribute("data-element-id");
        if (id && !idSet.has(id)) {
          el.classList.remove('selected');
        }
      });
      
      // Find all elements to select in the new selection
      for (const element of elements) {
        const id = element.getAttribute("data-element-id");
        if (id && idSet.has(id)) {
          // Add class if not already selected
          if (!element.classList.contains('selected')) {
            element.classList.add('selected');
          }
          elementsToSelect.push(element);
        }
      }
      
      // Update selectedElements array
      this.selectedElements = elementsToSelect;
      
      // Only notify if we're not already in a notification cycle
      if (!this.isNotifying) {
        const selectionData = this.getSelectionData();
        this.notifyListeners('selectionComplete', { 
          selectedElements: selectionData
        });
      }
    } finally {
      this.isSelectingById = false;
    }
  }
}